import { useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CatalogModelUpload } from "@/components/CatalogModelUpload";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Boxes, Plus, Upload, Search, Star, Edit3, Trash2, Eye, EyeOff, Archive, ArchiveRestore,
  Image as ImageIcon, X,
} from "lucide-react";

/**
 * Admin console tab for the 3D Print Shop catalog.
 *
 * Wraps the existing backend CRUD (`/api/admin/prints/catalog`) plus the
 * bulk STL/OBJ/ZIP upload endpoint. Admins can:
 *   • Bulk-drop STL/OBJ/ZIP files → each becomes a `draft` catalog row.
 *   • Fill in name, description, category, price, default scale, preview
 *     image, and flip the row to `published` so it shows on /prints/shop.
 *   • Feature a row (sorts to the top of the shop grid).
 *   • Archive a row (hides from public shop but keeps it around).
 *   • Delete permanently.
 */
export default function AdminPrintsCatalog() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editingItem, setEditingItem] = useState(null);   // null = creating new
  const [editorOpen, setEditorOpen] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const bulkInputRef = useRef(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-prints-catalog", statusFilter],
    queryFn: async () => (await api.get(
      `/admin/prints/catalog${statusFilter !== "all" ? `?status=${statusFilter}` : ""}`
    )).data,
    staleTime: 5_000,
  });
  const items = useMemo(() => data?.items || [], [data?.items]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((i) =>
      (i.name || "").toLowerCase().includes(q) ||
      (i.description || "").toLowerCase().includes(q) ||
      (i.category || "").toLowerCase().includes(q) ||
      (i.file_name || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const setStatus = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/admin/prints/catalog/${id}`, { status })).data,
    onSuccess: (_, v) => {
      toast.success(`Marked ${v.status}`);
      qc.invalidateQueries({ queryKey: ["admin-prints-catalog"] });
    },
    onError: (e) => toast.error(apiError(e, "Update failed")),
  });

  const toggleFeatured = useMutation({
    mutationFn: async ({ id, featured }) => (await api.patch(`/admin/prints/catalog/${id}`, { featured })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-prints-catalog"] }),
    onError: (e) => toast.error(apiError(e, "Toggle failed")),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/prints/catalog/${id}`)).data,
    onSuccess: () => {
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["admin-prints-catalog"] });
    },
    onError: (e) => toast.error(apiError(e, "Delete failed")),
  });

  const runBulkUpload = async () => {
    if (!bulkFiles.length) return;
    // Cloudflare + nginx front the production API and cap multipart bodies
    // around 100 MB per request. A big drop of 50 MB STL files easily blows
    // that, so we upload each file in its OWN request and aggregate the
    // results locally. Two side benefits: per-file progress is meaningful,
    // and a single bad file only skips itself.
    const MAX_PER_REQUEST = 90 * 1024 * 1024;
    setBulkUploading(true);
    const results = { ok: 0, skipped: [], failed: [] };
    try {
      for (let i = 0; i < bulkFiles.length; i++) {
        const f = bulkFiles[i];
        setBulkProgress({ current: i + 1, total: bulkFiles.length, name: f.name });
        if (f.size > MAX_PER_REQUEST) {
          results.failed.push({ name: f.name, reason: `File is ${(f.size / 1024 / 1024).toFixed(1)} MB — over the 90 MB per-request cap. Upload it on its own or shrink it first.` });
          continue;
        }
        const fd = new FormData();
        fd.append("files", f);
        try {
          // Let session/CSRF failures pass through the shared refresh interceptors
          // before preserving the existing per-file HTTP error presentation.
          const res = await api.post("/admin/prints/catalog/bulk-upload", fd)
            .catch((error) => { if (error.response) return error.response; throw error; });
          if (res.status >= 400) {
            let detail = `HTTP ${res.status}`;
            try {
              const j = res.data;
              detail = j.detail || detail;
            } catch { /* body might not be JSON if the edge intercepted */ }
            if (res.status === 413) {
              detail = `The server rejected this file as too large (${(f.size / 1024 / 1024).toFixed(1)} MB). Try uploading it alone.`;
            } else if (res.status === 500 || res.status === 502 || res.status === 504) {
              detail = `Server ${res.status} — file may be too large for the edge proxy (${(f.size / 1024 / 1024).toFixed(1)} MB). Try uploading it alone.`;
            }
            results.failed.push({ name: f.name, reason: detail });
            continue;
          }
          const json = res.data;
          for (const it of json.items || []) {
            if (it.skipped) results.skipped.push({ name: it.file_name || f.name, reason: it.reason });
            else results.ok += 1;
          }
        } catch (err) {
          results.failed.push({ name: f.name, reason: err.message || String(err) });
        }
      }
      // Aggregate feedback
      const bits = [];
      if (results.ok) bits.push(`${results.ok} uploaded`);
      if (results.skipped.length) bits.push(`${results.skipped.length} skipped`);
      if (results.failed.length) bits.push(`${results.failed.length} failed`);
      const summary = bits.join(" · ") || "Nothing uploaded";
      if (results.failed.length) {
        toast.error(summary, {
          duration: 8000,
          description: results.failed.slice(0, 3).map((f) => `${f.name}: ${f.reason}`).join("\n") + (results.failed.length > 3 ? `\n… and ${results.failed.length - 3} more` : ""),
        });
      } else {
        toast.success(summary);
      }
      setBulkFiles([]);
      if (bulkInputRef.current) bulkInputRef.current.value = "";
      qc.invalidateQueries({ queryKey: ["admin-prints-catalog"] });
    } finally {
      setBulkUploading(false);
      setBulkProgress(null);
    }
  };

  const openNew = () => { setEditingItem(null); setEditorOpen(true); };
  const openEdit = (it) => { setEditingItem(it); setEditorOpen(true); };

  return (
    <div className="max-w-6xl" data-testid="admin-prints-catalog-page">
      <div className="flex items-center gap-2 mb-4">
        <Boxes className="h-4 w-4 text-fuchsia-300" />
        <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-fuchsia-300">Print Shop · Catalog</span>
      </div>
      <div className="flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row mb-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl text-white">Manage catalog items</h1>
          <p className="text-white/55 text-[13px] mt-1 max-w-2xl">
            Drop STL / OBJ / ZIP files below to seed drafts, then fill in a name, price and preview image and flip to <span className="font-mono uppercase text-[10px] tracking-[0.2em] text-emerald-300">published</span>. Published rows show on the public <span className="font-mono uppercase text-[10px] tracking-[0.2em]">/prints/shop</span> immediately.
          </p>
        </div>
        <Button onClick={openNew} data-testid="admin-catalog-new"
          className="bg-fuchsia-400 text-black hover:bg-fuchsia-300 shrink-0">
          <Plus className="h-4 w-4 mr-1" /> New item
        </Button>
      </div>

      {/* Bulk upload drop zone */}
      <div className="rounded-2xl border border-dashed border-fuchsia-300/30 bg-fuchsia-400/[0.03] p-4 mb-5" data-testid="admin-catalog-bulk-upload">
        <div className="flex items-center gap-2 mb-2">
          <Upload className="h-4 w-4 text-fuchsia-300" />
          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-fuchsia-300">Bulk upload · STL, OBJ, ZIP</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={bulkInputRef}
            type="file"
            multiple
            accept=".stl,.obj,.zip"
            onChange={(e) => setBulkFiles(Array.from(e.target.files || []))}
            data-testid="admin-catalog-bulk-input"
            className="text-[12px] text-white/70 file:mr-3 file:rounded-md file:border-0 file:bg-white/10 file:px-3 file:py-1.5 file:text-white file:text-[11px] file:font-mono file:uppercase file:tracking-[0.18em] file:hover:bg-white/15"
          />
          {bulkFiles.length > 0 && (
            <>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/50">
                {bulkFiles.length} file{bulkFiles.length === 1 ? "" : "s"} queued
              </span>
              <Button size="sm" onClick={runBulkUpload} disabled={bulkUploading}
                data-testid="admin-catalog-bulk-submit"
                className="bg-fuchsia-400 text-black hover:bg-fuchsia-300 h-7 text-[11px]">
                {bulkUploading ? "Uploading…" : "Upload"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setBulkFiles([]); if (bulkInputRef.current) bulkInputRef.current.value = ""; }}
                className="border-white/15 text-white/70 h-7 text-[11px]">
                Clear
              </Button>
            </>
          )}
        </div>
        <div className="text-[10px] text-white/40 mt-2">
          Each STL / OBJ becomes a <em>draft</em> catalog row (name pulled from the filename). ZIPs are walked and any STL/OBJ inside is added too. Non-STL/OBJ files are skipped. Files upload one at a time (proxy caps single requests around 90&nbsp;MB) so a single oversize file only skips itself.
        </div>
        {bulkProgress && (
          <div className="mt-2 flex items-center gap-2 rounded-md bg-black/30 border border-white/10 px-2 py-1.5" data-testid="admin-catalog-bulk-progress">
            <div className="h-1.5 flex-1 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full bg-fuchsia-400 transition-all" style={{ width: `${(bulkProgress.current / Math.max(1, bulkProgress.total)) * 100}%` }} />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 shrink-0">
              {bulkProgress.current}/{bulkProgress.total} · <span className="text-white/80 truncate max-w-[200px] inline-block align-middle">{bulkProgress.name}</span>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {["all", "draft", "published", "archived"].map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            data-testid={`admin-catalog-filter-${s}`}
            className={`font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-full border transition-colors ${
              statusFilter === s
                ? "border-fuchsia-300/60 bg-fuchsia-400/10 text-fuchsia-300"
                : "border-white/10 text-white/50 hover:text-white hover:border-white/30"
            }`}>
            {s}
          </button>
        ))}
        <div className="relative flex-1 min-w-[220px] ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            data-testid="admin-catalog-search"
            placeholder="Search name / file / category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-9"
          />
        </div>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="text-white/50 text-sm">Loading catalog…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-black/30 p-8 text-center text-white/60" data-testid="admin-catalog-empty">
          {items.length === 0
            ? "No catalog rows yet — drop STL/OBJ files above or hit New item."
            : "No rows match this filter."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-catalog-grid">
          {filtered.map((it) => (
            <CatalogRow
              key={it.id}
              item={it}
              onEdit={() => openEdit(it)}
              onPublish={() => setStatus.mutate({ id: it.id, status: "published" })}
              onUnpublish={() => setStatus.mutate({ id: it.id, status: "draft" })}
              onArchive={() => setStatus.mutate({ id: it.id, status: "archived" })}
              onRestore={() => setStatus.mutate({ id: it.id, status: "draft" })}
              onFeature={() => toggleFeatured.mutate({ id: it.id, featured: !it.featured })}
              onDelete={() => {
                if (window.confirm(`Delete "${it.name}" permanently? This cannot be undone.`)) {
                  del.mutate(it.id);
                }
              }}
            />
          ))}
        </div>
      )}

      <CatalogEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        item={editingItem}
        onSaved={() => { setEditorOpen(false); refetch(); }}
      />
    </div>
  );
}

function CatalogRow({ item, onEdit, onPublish, onUnpublish, onArchive, onRestore, onFeature, onDelete }) {
  const statusColor = { published: "text-emerald-300", draft: "text-amber-300", archived: "text-white/40" }[item.status] || "text-white/60";
  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 overflow-hidden flex flex-col" data-testid={`admin-catalog-row-${item.id}`}>
      <div className="relative aspect-[4/3] bg-gradient-to-br from-fuchsia-400/10 to-amber-400/5 overflow-hidden">
        {item.preview_image_url ? (
          <img src={item.preview_image_url} alt={item.name} loading="lazy"
            className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Boxes className="h-14 w-14 text-fuchsia-300/40" />
          </div>
        )}
        {item.featured && (
          <div className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full border border-amber-300/40 bg-amber-400/20 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.22em] text-amber-300">
            <Star className="h-2.5 w-2.5 fill-current" /> Featured
          </div>
        )}
        <div className={`absolute top-2 right-2 rounded-full border border-white/15 bg-black/60 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.22em] ${statusColor}`}>
          {item.status}
        </div>
      </div>

      <div className="p-3 flex flex-col gap-2 flex-1">
        <div className="min-w-0">
          <div className="text-white font-display text-base truncate" title={item.name}>{item.name}</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45 mt-0.5 flex items-center gap-2">
            {item.category ? <span>{item.category}</span> : <span className="text-white/25">no category</span>}
            <span>·</span>
            <span>{item.base_price_usd != null ? `$${Number(item.base_price_usd).toFixed(2)}` : "custom quote"}</span>
          </div>
          {item.file_name && (
            <div className="mt-1 text-[10px] text-white/40 truncate" title={item.file_name}>
              {item.file_name} {item.file_size_bytes ? `· ${(item.file_size_bytes / 1024 / 1024).toFixed(1)}MB` : ""}
            </div>
          )}
        </div>

        <div className="mt-auto flex flex-wrap gap-1.5">
          <Button size="sm" variant="outline" onClick={onEdit}
            data-testid={`admin-catalog-row-${item.id}-edit`}
            className="h-7 px-2 text-[11px] border-white/15 text-white/80 hover:bg-white/5">
            <Edit3 className="h-3 w-3 mr-1" /> Edit
          </Button>
          {item.status === "draft" && (
            <Button size="sm" onClick={onPublish}
              data-testid={`admin-catalog-row-${item.id}-publish`}
              className="h-7 px-2 text-[11px] bg-emerald-400 text-black hover:bg-emerald-300">
              <Eye className="h-3 w-3 mr-1" /> Publish
            </Button>
          )}
          {item.status === "published" && (
            <Button size="sm" onClick={onUnpublish}
              data-testid={`admin-catalog-row-${item.id}-unpublish`}
              className="h-7 px-2 text-[11px] bg-amber-400 text-black hover:bg-amber-300">
              <EyeOff className="h-3 w-3 mr-1" /> Unpublish
            </Button>
          )}
          {item.status !== "archived" ? (
            <Button size="sm" variant="outline" onClick={onArchive}
              data-testid={`admin-catalog-row-${item.id}-archive`}
              className="h-7 px-2 text-[11px] border-white/15 text-white/70 hover:bg-white/5">
              <Archive className="h-3 w-3 mr-1" /> Archive
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={onRestore}
              data-testid={`admin-catalog-row-${item.id}-restore`}
              className="h-7 px-2 text-[11px] border-white/15 text-white/70 hover:bg-white/5">
              <ArchiveRestore className="h-3 w-3 mr-1" /> Restore
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onFeature}
            data-testid={`admin-catalog-row-${item.id}-feature`}
            className={`h-7 px-2 text-[11px] ${item.featured ? "border-amber-300/40 text-amber-300 hover:bg-amber-400/10" : "border-white/15 text-white/60 hover:bg-white/5"}`}>
            <Star className={`h-3 w-3 mr-1 ${item.featured ? "fill-current" : ""}`} /> {item.featured ? "Unfeature" : "Feature"}
          </Button>
          <Button size="sm" variant="outline" onClick={onDelete}
            data-testid={`admin-catalog-row-${item.id}-delete`}
            className="h-7 px-2 text-[11px] border-red-400/30 text-red-300 hover:bg-red-400/10 ml-auto">
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function CatalogEditorDialog({ open, onOpenChange, item, onSaved }) {
  const isEdit = !!item;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [basePrice, setBasePrice] = useState("");
  const [defaultScale, setDefaultScale] = useState("1.0");
  const [featured, setFeatured] = useState(false);
  const [status, setStatus] = useState("draft");
  const [previewData, setPreviewData] = useState("");   // data URL or existing URL
  const [saving, setSaving] = useState(false);
  const [model, setModel] = useState(null);
  const [modelBusy, setModelBusy] = useState(false);

  // Reset fields whenever the item to edit changes / dialog opens.
  const seedFrom = (src) => {
    setName(src?.name || "");
    setDescription(src?.description || "");
    setCategory(src?.category || "");
    setBasePrice(src?.base_price_usd != null ? String(src.base_price_usd) : "");
    setDefaultScale(String(src?.default_scale ?? "1.0"));
    setFeatured(!!src?.featured);
    setStatus(src?.status || "draft");
    setPreviewData(src?.preview_image_url || "");
    setModel(src?.file_ref ? { file_ref: src.file_ref, file_ext: src.file_ext, file_name: src.file_name, file_size_bytes: src.file_size_bytes } : null);
    setModelBusy(false);
  };

  const wasOpen = useRef(false);
  if (open && !wasOpen.current) {
    wasOpen.current = true;
    seedFrom(item);
  } else if (!open && wasOpen.current) {
    wasOpen.current = false;
  }

  const onPickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(png|jpeg|webp|gif)$/i.test(f.type)) {
      e.target.value = "";
      toast.error("Choose a PNG, JPEG, WebP or GIF thumbnail. STL/OBJ belongs in the 3D model field.");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Preview image must be under 5 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPreviewData(String(reader.result || ""));
    reader.readAsDataURL(f);
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Name is required.");
    if (modelBusy) return;
    if (status === "published" && !model?.file_ref) return toast.error("Attach an STL or OBJ model before publishing.");
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      category: category.trim() || null,
      base_price_usd: basePrice === "" ? null : Number(basePrice),
      default_scale: Number(defaultScale) || 1.0,
      featured,
      status,
      preview_image_url: previewData || null,
      ...(model || {}),
    };
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/admin/prints/catalog/${item.id}`, payload);
        toast.success("Saved");
      } else {
        await api.post(`/admin/prints/catalog`, payload);
        toast.success("Created");
      }
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[hsl(222_22%_7%)] border-white/10" data-testid="admin-catalog-editor">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Boxes className="h-4 w-4 text-fuchsia-300" />
            {isEdit ? `Edit "${item?.name || ""}"` : "New catalog item"}
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            {isEdit
              ? "Update this catalog row. Set status to Published to make it live on /prints/shop."
              : "New catalog item. Model file and preview image are separate attachments."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm max-h-[65vh] overflow-y-auto pr-1">
          <CatalogModelUpload value={model} busy={modelBusy} onBusy={setModelBusy} onChange={setModel} />
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              data-testid="admin-catalog-editor-name"
              className="mt-1 bg-white/5 border-white/10 text-white" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Description</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
              maxLength={1000} data-testid="admin-catalog-editor-desc"
              className="mt-1 bg-white/5 border-white/10 text-white min-h-[70px]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Category</label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. Miniatures"
                data-testid="admin-catalog-editor-category"
                className="mt-1 bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Base price (USD)</label>
              <Input value={basePrice} onChange={(e) => setBasePrice(e.target.value.replace(/[^0-9.]/g, ""))}
                placeholder="Leave blank for quote-only"
                data-testid="admin-catalog-editor-price"
                className="mt-1 bg-white/5 border-white/10 text-white" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Default scale</label>
              <Input value={defaultScale} onChange={(e) => setDefaultScale(e.target.value.replace(/[^0-9.]/g, ""))}
                data-testid="admin-catalog-editor-scale"
                className="mt-1 bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}
                data-testid="admin-catalog-editor-status"
                className="mt-1 w-full h-9 rounded-md border border-white/10 bg-white/5 text-white text-sm px-2">
                <option value="draft">draft</option>
                <option value="published">published</option>
                <option value="archived">archived</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-white/70">
            <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)}
              data-testid="admin-catalog-editor-featured" className="accent-amber-400" />
            <Star className="h-3 w-3 text-amber-300" /> Featured
          </label>

          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55 flex items-center gap-1">
              <ImageIcon className="h-3 w-3" /> Preview image
            </label>
            <div className="mt-1 flex items-start gap-3">
              <div className="h-20 w-20 rounded-lg border border-white/10 bg-black/40 overflow-hidden flex items-center justify-center shrink-0">
                {previewData ? (
                  <img src={previewData} alt="preview" className="h-full w-full object-cover" />
                ) : (
                  <ImageIcon className="h-6 w-6 text-white/25" />
                )}
              </div>
              <div className="flex-1">
                <input type="file" accept="image/*" onChange={onPickImage}
                  data-testid="admin-catalog-editor-image"
                  className="text-[12px] text-white/70 file:mr-2 file:rounded-md file:border-0 file:bg-white/10 file:px-2 file:py-1 file:text-white file:text-[10px] file:font-mono file:uppercase file:tracking-[0.18em] file:hover:bg-white/15" />
                {previewData && (
                  <Button size="sm" variant="outline" onClick={() => setPreviewData("")}
                    data-testid="admin-catalog-editor-image-clear"
                    className="mt-2 h-6 px-2 text-[10px] border-white/15 text-white/70">
                    <X className="h-3 w-3 mr-1" /> Remove image
                  </Button>
                )}
                <div className="mt-1 text-[10px] text-white/40">Max 5 MB. Uploaded to shared object storage on save.</div>
              </div>
            </div>
          </div>

        </div>

        <DialogFooter>
          <Button data-testid="admin-catalog-editor-cancel" variant="ghost" onClick={() => onOpenChange(false)} className="text-white/70">Cancel</Button>
          <Button onClick={save} disabled={saving || modelBusy || !name.trim()}
            data-testid="admin-catalog-editor-save"
            className="bg-fuchsia-400 text-black hover:bg-fuchsia-300">
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
